import type React from "react"
import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react"

export type MarkerData = {
  id: string
  lat: number
  lng: number
  type?: string
  iconUrl?: string
  svgContent?: string
  htmlContent?: string
  title?: string
  clickable?: boolean
  visible?: boolean
  zIndex?: number
  onClick?: () => void
  [key: string]: any
}

type MapMarkersContextProps = {
  addMarker: (marker: MarkerData) => void
  updateMarker: (id: string, data: Partial<MarkerData>) => void
  removeMarker: (id: string) => void
  clearMarkers: () => void
  markers: MarkerData[]
  mapInstance: any
  setMapInstance: (instance: any) => void
}

const MapMarkersContext = createContext<MapMarkersContextProps | undefined>(
  undefined,
)

export const MapMarkersProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [markers, setMarkers] = useState<MarkerData[]>([])
  const mapInstanceRef = useRef<any>(null)
  const [forceRender, setForceRender] = useState(0) // State to force re-render

  const markerInstancesRef = useRef<Map<string, any>>(new Map())

  const setMapInstance = useCallback((instance: any) => {
    mapInstanceRef.current = instance
    setForceRender(c => c + 1) // Force re-render for consumers
  }, [])

  const createOrUpdateNaverMarker = useCallback((marker: MarkerData) => {
    const map = mapInstanceRef.current
    if (!map || !window.naver) return

    const position = new window.naver.maps.LatLng(marker.lat, marker.lng)
    let markerInstance = markerInstancesRef.current.get(marker.id)

    let icon
    if (marker.htmlContent) {
      icon = {
        content: marker.htmlContent,
        size: new window.naver.maps.Size(48, 52),
        anchor: new window.naver.maps.Point(24, 44),
      }
    } else if (marker.svgContent) {
      icon = {
        content: marker.svgContent,
        size: new window.naver.maps.Size(48, 52),
        anchor: new window.naver.maps.Point(24, 44),
      }
    } else if (marker.iconUrl) {
      icon = {
        url: marker.iconUrl,
        size: new window.naver.maps.Size(48, 52),
        origin: new window.naver.maps.Point(0, 0),
        anchor: new window.naver.maps.Point(24, 44),
      }
    }

    if (markerInstance) {
      markerInstance.setPosition(position)
      if (icon) markerInstance.setIcon(icon)
      markerInstance.setTitle(marker.title || "")
      markerInstance.setZIndex(marker.zIndex || 0)
      if (marker.visible !== undefined)
        markerInstance.setVisible(marker.visible)
    } else {
      markerInstance = new window.naver.maps.Marker({
        position,
        map: map,
        icon,
        title: marker.title,
        clickable: true,
        visible: true,
        zIndex: 100,
      })

      if (marker.onClick) {
        window.naver.maps.Event.addListener(
          markerInstance,
          "click",
          marker.onClick,
        )
      }
      markerInstancesRef.current.set(marker.id, markerInstance)
    }
    markerInstance.data = { ...marker }
  }, [])

  const addMarker = useCallback(
    (marker: MarkerData) => {
      createOrUpdateNaverMarker(marker)
      setMarkers(prev => {
        if (prev.some(m => m.id === marker.id)) {
          return prev.map(m => (m.id === marker.id ? { ...m, ...marker } : m))
        }
        return [...prev, marker]
      })
    },
    [createOrUpdateNaverMarker],
  )

  const updateMarker = useCallback(
    (id: string, data: Partial<MarkerData>) => {
      setMarkers(prev => {
        const markerIndex = prev.findIndex(m => m.id === id)
        if (markerIndex === -1) return prev
        const updatedMarkers = [...prev]
        const updatedMarker = { ...updatedMarkers[markerIndex], ...data }
        updatedMarkers[markerIndex] = updatedMarker
        createOrUpdateNaverMarker(updatedMarker)
        return updatedMarkers
      })
    },
    [createOrUpdateNaverMarker],
  )

  const removeMarker = useCallback((id: string) => {
    const markerInstance = markerInstancesRef.current.get(id)
    if (markerInstance) {
      markerInstance.setMap(null)
      markerInstancesRef.current.delete(id)
    }
    setMarkers(prev => prev.filter(marker => marker.id !== id))
  }, [])

  const clearMarkers = useCallback(() => {
    markerInstancesRef.current.forEach(marker => {
      marker.setMap(null)
    })
    markerInstancesRef.current.clear()
    setMarkers([])
  }, [])

  const contextValue = useMemo(
    () => ({
      markers,
      addMarker,
      updateMarker,
      removeMarker,
      clearMarkers,
      mapInstance: mapInstanceRef.current,
      setMapInstance,
    }),
    [
      markers,
      addMarker,
      updateMarker,
      removeMarker,
      clearMarkers,
      setMapInstance,
      forceRender,
    ],
  )

  return (
    <MapMarkersContext.Provider value={contextValue}>
      {children}
    </MapMarkersContext.Provider>
  )
}

export const useMapMarkers = () => {
  const context = useContext(MapMarkersContext)
  if (context === undefined) {
    throw new Error("useMapMarkers must be used within a MapMarkersProvider")
  }
  return context
}
